import { Info, Save, Settings } from 'lucide-react';
import { useState } from 'react';

import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { SectionCard } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { toNumber } from '@/lib/utils';

import { ControlSelect } from '../components/meta-campos';
import { MetasNav } from '../components/metas-nav';
import { useMetasConfiguracion } from '../hooks/use-metas';
import { useGuardarConfiguracionMetas } from '../hooks/use-metas-mutations';
import type { GoalSettings } from '../metas.types';

/** Estado del formulario: todo viaja como texto, igual que en la base. */
interface FormState {
  auto_evaluation_enabled: boolean;
  notification_threshold: string;
  default_goal_duration_days: string;
  weekly_evaluation_day: string;
  monthly_evaluation_day: string;
}

const DIAS_SEMANA = [
  { value: '1', label: 'Lunes' },
  { value: '2', label: 'Martes' },
  { value: '3', label: 'Miércoles' },
  { value: '4', label: 'Jueves' },
  { value: '5', label: 'Viernes' },
  { value: '6', label: 'Sábado' },
  { value: '7', label: 'Domingo' },
] as const;

const DIAS_MES = Array.from({ length: 31 }, (_, index) => String(index + 1));

function estadoInicial(settings: GoalSettings | undefined): FormState {
  return {
    auto_evaluation_enabled: settings?.auto_evaluation_enabled === '1',
    notification_threshold: settings?.notification_threshold ?? '80',
    default_goal_duration_days: settings?.default_goal_duration_days ?? '30',
    weekly_evaluation_day: settings?.weekly_evaluation_day ?? '1',
    monthly_evaluation_day: settings?.monthly_evaluation_day ?? '1',
  };
}

/**
 * Configuración del módulo de metas (`/metas/configuracion`).
 *
 * Estos valores viven en la tabla de parámetros del sistema
 * (`auto_evaluation_enabled`, `notification_threshold`, `default_goal_duration_days`,
 * `weekly_evaluation_day`, `monthly_evaluation_day`) y los usa la evaluación
 * automática del backend.
 */
export function MetasConfiguracionPage() {
  useDocumentTitle('Metas · Configuración');

  const configuracion = useMetasConfiguracion();

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wider text-ink-400">Seguimiento</p>
          <h1 className="mt-1 flex items-center gap-2 text-2xl font-bold tracking-tight text-ink-900">
            <Settings className="size-6 text-brand-500" aria-hidden="true" />
            Configuración de metas
          </h1>
          <p className="mt-1 text-sm text-ink-500">
            Parámetros que usan la evaluación automática y los avisos de seguimiento.
          </p>
        </div>

        <MetasNav active="configuracion" className="justify-end" />
      </header>

      {configuracion.isError ? (
        <Alert variant="error" title="No se pudo cargar la configuración">
          <div className="flex flex-wrap items-center gap-3">
            <span>Verifica la conexión con el servidor e inténtalo nuevamente.</span>
            <Button
              size="sm"
              variant="outline"
              onClick={() => void configuracion.refetch()}
              loading={configuracion.isRefetching}
            >
              Reintentar
            </Button>
          </div>
        </Alert>
      ) : null}

      {configuracion.isPending || !configuracion.data ? (
        <Skeleton className="h-80 rounded-card" />
      ) : (
        <FormularioConfiguracion settings={configuracion.data} />
      )}
    </div>
  );
}

function FormularioConfiguracion({ settings }: { settings: GoalSettings }) {
  const guardar = useGuardarConfiguracionMetas();

  const [valores, setValores] = useState<FormState>(() => estadoInicial(settings));
  const [errores, setErrores] = useState<Record<string, string>>({});
  const [aviso, setAviso] = useState<{ tipo: 'success' | 'error'; texto: string } | null>(null);

  const cambiar = <C extends keyof FormState>(campo: C, valor: FormState[C]) => {
    setValores((prev) => ({ ...prev, [campo]: valor }));
  };

  const enviar = () => {
    const encontrados: Record<string, string> = {};
    const umbral = toNumber(valores.notification_threshold);
    const duracion = toNumber(valores.default_goal_duration_days);

    if (!(umbral >= 0 && umbral <= 100)) {
      encontrados.notification_threshold = 'El umbral debe estar entre 0 y 100.';
    }
    if (!(duracion >= 1 && duracion <= 365)) {
      encontrados.default_goal_duration_days = 'La duración debe estar entre 1 y 365 días.';
    }

    setErrores(encontrados);
    setAviso(null);

    if (Object.keys(encontrados).length > 0) {
      setAviso({ tipo: 'error', texto: 'Revisa los campos marcados antes de guardar.' });
      return;
    }

    guardar.mutate(
      {
        auto_evaluation_enabled: valores.auto_evaluation_enabled ? '1' : '0',
        notification_threshold: String(umbral),
        default_goal_duration_days: String(duracion),
        weekly_evaluation_day: valores.weekly_evaluation_day,
        monthly_evaluation_day: valores.monthly_evaluation_day,
      },
      {
        onSuccess: () =>
          setAviso({ tipo: 'success', texto: 'La configuración de metas quedó guardada.' }),
        onError: (error) =>
          setAviso({ tipo: 'error', texto: error.message || 'No se pudo guardar la configuración.' }),
      },
    );
  };

  return (
    <div className="space-y-6">
      {aviso ? (
        <Alert
          variant={aviso.tipo === 'success' ? 'success' : 'error'}
          title={aviso.tipo === 'success' ? 'Listo' : 'Revisa el dato'}
        >
          {aviso.texto}
        </Alert>
      ) : null}

      <SectionCard title="Evaluación" description="Cómo y cuándo se evalúa el avance">
            <div className="space-y-4">
              <Checkbox
                label="Evaluación automática"
                hint="El sistema registra evaluaciones del avance sin intervención del usuario."
                checked={valores.auto_evaluation_enabled}
                onChange={(event) => cambiar('auto_evaluation_enabled', event.target.checked)}
              />

              <div className="grid gap-4 sm:grid-cols-2">
                <Input
                  label="Umbral de notificación (%)"
                  type="number"
                  min={0}
                  max={100}
                  step="1"
                  inputMode="numeric"
                  value={valores.notification_threshold}
                  error={errores.notification_threshold}
                  hint="Avance a partir del cual se avisa al responsable."
                  onChange={(event) => cambiar('notification_threshold', event.target.value)}
                />

                <Input
                  label="Duración por defecto (días)"
                  type="number"
                  min={1}
                  max={365}
                  step="1"
                  inputMode="numeric"
                  value={valores.default_goal_duration_days}
                  error={errores.default_goal_duration_days}
                  hint="Se propone al crear una meta nueva."
                  onChange={(event) => cambiar('default_goal_duration_days', event.target.value)}
                />
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <ControlSelect
                  label="Día de evaluación semanal"
                  value={valores.weekly_evaluation_day}
                  hint="Día en que corre la evaluación de la semana."
                  onChange={(event) => cambiar('weekly_evaluation_day', event.target.value)}
                >
                  {DIAS_SEMANA.map((dia) => (
                    <option key={dia.value} value={dia.value}>
                      {dia.label}
                    </option>
                  ))}
                </ControlSelect>

                <ControlSelect
                  label="Día de evaluación mensual"
                  value={valores.monthly_evaluation_day}
                  hint="Día del mes en que se cierra la evaluación."
                  onChange={(event) => cambiar('monthly_evaluation_day', event.target.value)}
                >
                  {DIAS_MES.map((dia) => (
                    <option key={dia} value={dia}>
                      Día {dia}
                    </option>
                  ))}
                </ControlSelect>
              </div>
            </div>
          </SectionCard>

          <SectionCard title="Vista previa" description="Cómo queda el seguimiento con estos valores">
            <ul className="space-y-2 text-sm text-ink-700">
              <li className="flex items-start gap-2">
                <Info className="mt-0.5 size-4 shrink-0 text-ink-400" aria-hidden="true" />
                {valores.auto_evaluation_enabled
                  ? 'La evaluación automática está activa: el backend registra el avance por su cuenta.'
                  : 'La evaluación automática está desactivada: el avance se registra sólo al recalcular o evaluar a mano.'}
              </li>
              <li className="flex items-start gap-2">
                <Info className="mt-0.5 size-4 shrink-0 text-ink-400" aria-hidden="true" />
                Se avisa desde el {valores.notification_threshold || '0'} % de avance.
              </li>
              <li className="flex items-start gap-2">
                <Info className="mt-0.5 size-4 shrink-0 text-ink-400" aria-hidden="true" />
                Las metas nuevas duran {valores.default_goal_duration_days || '0'} días por defecto.
              </li>
              <li className="flex items-start gap-2">
                <Info className="mt-0.5 size-4 shrink-0 text-ink-400" aria-hidden="true" />
                Evaluación semanal:{' '}
                {DIAS_SEMANA.find((dia) => dia.value === valores.weekly_evaluation_day)?.label ??
                  'sin definir'}
                ; evaluación mensual: día {valores.monthly_evaluation_day}.
              </li>
            </ul>
          </SectionCard>

          <div className="flex justify-end">
            <Button
              leftIcon={<Save className="size-4" aria-hidden="true" />}
              loading={guardar.isPending}
              onClick={enviar}
            >
              Guardar configuración
            </Button>
          </div>
    </div>
  );
}
